import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Shop } from "@/types/shop";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import dayjs from "dayjs";
import PlannerAvailabilityCalendar from "./components/PlannerAvailabilityCalendar";
import { buildSearch, getQueryParams } from "@/utils/query";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { clearAvailability, fetchMonthlyAvailability } from "@/store/slices/availabilitySlice";
import DailyCombinationSection from "@/components/combinations/DailyCombinationSection";
import { ShopItem } from "../reservations/detail";
import ShopNaverMap from "@/components/common/NaverMap/ShopNaverMap";
import styled from 'styled-components'
import { GA_EVENT, trackEvent } from "@/utils/gaUtil";
function parseCommaString(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

function parseCommaNumber(value: string | undefined): number[] {
  if (!value) return [];
  return value
    .split(",")
    .map((v) => Number(v))
    .filter((v) => !Number.isNaN(v));
}

const CombinationWrap = styled.div`
  @media screen and (min-width: 1024px) {
    height: calc(100vh - 176px);
    overflow: auto;
  }
`

type RightTab = "map" | "schedule";

export default function MonthlyAvailability() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = getQueryParams(location.search);
  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState<RightTab>("map");
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 800);
  const [lastResultViewKey, setLastResultViewKey] = useState<string | null>(null);
  const { dailyAvailCountMap } = useAppSelector((state) => state.availability);

  const {
    selectedShopIds: selectedShopIdsStr = "",
    fixedShopIds: fixedShopIdsStr = "",
    tourSize = "3",
    month: monthStr = '2026-10',
    selectedDate,
  } = query;

  const { shops } = useSelector((state: RootState) => state.shop);

  const shopMap = useMemo(() => {
    return (shops || [])
      .map((v) => ({ ...v, lng: v.lon }))
      .reduce((acc: Record<number, ShopItem>, shop: ShopItem) => {
        acc[Number(shop.id)] = shop;
        return acc;
      }, {});
  }, [shops]);

  const selectedShopIds = useMemo(
    () => parseCommaString(selectedShopIdsStr),
    [selectedShopIdsStr]
  );

  const slots = selectedShopIds
    .map((id) => shopMap[Number(id)])
    .filter(Boolean);

  const fixedShopIds = useMemo(
    () => parseCommaNumber(fixedShopIdsStr),
    [fixedShopIdsStr]
  );

  const currentMonth = useMemo(() => {
    const parsed = monthStr ? dayjs(monthStr, "YYYY-MM", true) : dayjs();
    return parsed.isValid()
      ? parsed.startOf("month").toDate()
      : dayjs().startOf("month").toDate();
  }, [monthStr]);

  const selectedVendors = useMemo(() => {
    return shops.filter((shop) => selectedShopIds.includes(String(shop.id)));
  }, [shops, selectedShopIds]);

  const [showVendorModal, setShowVendorModal] = useState(false);
  const [modalVendors, setModalVendors] = useState<Shop[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const onResize = () => {
      setIsDesktop(window.innerWidth >= 800);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const updateSelectedDate = (dateStr: string) => {
    navigate({
      pathname: "/monthly-availability",
      search: buildSearch({
        selectedShopIds: selectedShopIds.join(","),
        fixedShopIds: fixedShopIds.join(","),
        tourSize,
        month: monthStr,
        selectedDate: dateStr,
      }),
    }, { replace: true });
  };

  const updateMonthlyQuery = (next: {
    selectedShopIds?: string[];
    fixedShopIds?: number[];
    tourSize?: string;
    month?: string;
  }) => {
    const nextSearch = buildSearch({
      selectedShopIds: (next.selectedShopIds ?? selectedShopIds).join(","),
      fixedShopIds: (next.fixedShopIds ?? fixedShopIds).join(","),
      tourSize: next.tourSize ?? tourSize,
      month: next.month ?? dayjs(currentMonth).format("YYYY-MM"),
    });

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch,
      },
      { replace: true }
    );
  };

  const openModal = () => {
    setModalVendors([...selectedVendors]);
    setSearchQuery("");
    setShowVendorModal(true);
  };

  const toggleModalVendor = (shop: Shop) => {
    const exists = modalVendors.some((vendor) => vendor.id === shop.id);

    if (exists) {
      setModalVendors((prev) => prev.filter((vendor) => vendor.id !== shop.id));
      return;
    }

    if (modalVendors.length >= 3) return;
    setModalVendors((prev) => [...prev, shop]);
  };

  const applyVendors = () => {
    if (modalVendors.length === 0) return;

    updateMonthlyQuery({
      selectedShopIds: modalVendors.map((vendor) => String(vendor.id)),
    });

    setShowVendorModal(false);
  };

  const removeVendor = (shopId: number) => {
    const nextIds = selectedVendors
      .filter((vendor) => vendor.id !== shopId)
      .map((vendor) => String(vendor.id));

    updateMonthlyQuery({
      selectedShopIds: nextIds,
    });
  };

  const filteredVendors = useMemo(() => {
    const keyword = searchQuery.trim().toLowerCase();

    if (!keyword) return shops;

    return shops.filter((shop) =>
      shop.shopName.toLowerCase().includes(keyword)
    );
  }, [shops, searchQuery]);

  useEffect(() => {
    if (selectedVendors.length === 0) {
      dispatch(clearAvailability());
      return;
    }
    
    dispatch(
      fetchMonthlyAvailability({
        month: dayjs(currentMonth).format("YYYY-MM"),
        tourSize,
        selectedShopIds: selectedVendors.map((shop) => shop.id),
        fixedShopIds,
      })
    );
  }, [dispatch, currentMonth, tourSize, fixedShopIdsStr, selectedShopIdsStr, shops]);
  useEffect(() => {
    if (selectedVendors.length === 0) return;

    const resultCount = Object.values(dailyAvailCountMap).reduce(
      (sum, count) => sum + Number(count || 0),
      0
    );

    const resultViewKey = [
      dayjs(currentMonth).format("YYYY-MM"),
      tourSize,
      selectedShopIds.join(","),
      fixedShopIds.join(","),
      resultCount,
    ].join("|");

    if (lastResultViewKey === resultViewKey) return;
    try {
      trackEvent(GA_EVENT.COMBINATION_RESULT_VIEW, {
        booking_path: "COMBINATION",
        month: dayjs(currentMonth).format("YYYY-MM"),
        tour_size: Number(tourSize),
        selected_shop_count: selectedVendors.length,
        fixed_shop_count: fixedShopIds.length,
        selected_shop_ids: selectedShopIds.join(","),
        fixed_shop_ids: fixedShopIds.join(","),
        result_count: resultCount,
        available_date_count: Object.values(dailyAvailCountMap).filter(
          (count) => Number(count || 0) > 0
        ).length,
        has_result: resultCount > 0 ? 1 : 0,
      });
    } catch (e) {

    }

    setLastResultViewKey(resultViewKey);
  }, [
    currentMonth,
    dailyAvailCountMap,
    fixedShopIds,
    lastResultViewKey,
    selectedShopIds,
    selectedVendors.length,
    tourSize,
  ]);
  const handleDateClick = (dateStr: string) => {
    const count = dailyAvailCountMap[dateStr] || 0;
    if (count <= 0) return;

    if (!isDesktop) {
      navigate({
        pathname: "/daily-schedule",
        search: buildSearch({
          selectedShopIds: selectedShopIds.join(","),
          fixedShopIds: fixedShopIds.join(","),
          tourSize,
          month: monthStr,
          selectedDate: dateStr,
        }),
        
      }
    );
      return;
    }

    setActiveTab("schedule");
    updateSelectedDate(dayjs(dateStr).format("YYYY-MM-DD"));
  };

  const changeMonth = (direction: number) => {
    const nextMonth = dayjs(currentMonth)
      .add(direction, "month")
      .format("YYYY-MM");

    updateMonthlyQuery({ month: nextMonth });
  };

  const handlePrevMonth = () => changeMonth(-1);
  const handleNextMonth = () => changeMonth(1);

  const monthNames = [
    "1월", "2월", "3월", "4월", "5월", "6월",
    "7월", "8월", "9월", "10월", "11월", "12월",
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col border-x border-gray-200">
      <div className="bg-white px-5 py-4 shadow-sm border-b border-gray-100">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl text-gray-700"></i>
          </button>

          <h2 className="text-md font-bold text-gray-900">
            날짜별 일정
          </h2>

          <div className="flex items-center gap-1">
            <button
              onClick={openModal}
              className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-edit-line text-xl"></i>
            </button>
            {/* <button
              onClick={handlePrevMonth}
              className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl text-gray-700"></i>
            </button> */}
            {/* <button
              onClick={handleNextMonth}
              className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl text-gray-700"></i>
            </button> */}
          </div>
        </div>
      </div>

      <div className="bg-white px-5 py-3 border-b border-gray-100">
        <div className="flex flex-wrap gap-2 items-center">
          {selectedVendors.map((shop) => (
            <div
              key={shop.id}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
            >
              <span className="whitespace-nowrap">{shop.shopName}</span>
              <button
                onClick={() => removeVendor(shop.id)}
                className="w-4 h-4 flex items-center justify-center hover:bg-blue-100 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </div>
          ))}

          {/* {selectedVendors.length < 3 && (
            <button
              onClick={openModal}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-sm"></i>
              업체 추가
            </button>
          )}

          {selectedVendors.length > 0 && (
            <button
              onClick={openModal}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-200 text-gray-500 rounded-full text-xs font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap ml-auto"
            >
              <i className="ri-edit-line text-sm"></i>
              <span>편집</span>
            </button>
          )} */}
        </div>
      </div>

      {isDesktop ? (
        <div className="flex flex-1 min-h-0 gap-0" style={{maxHeight: '100%'}}>
          <div className="flex-1 flex flex-col border-r border-[#E5E7EB] bg-[#F5F6F8] overflow-y-auto">
            <PlannerAvailabilityCalendar
              currentMonth={currentMonth}
              availability={dailyAvailCountMap}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              onDateClick={handleDateClick}
              selectedDate={selectedDate}
            />
            
          </div>

          <div className="flex-1 flex flex-col bg-white min-h-0">
            <div className="flex border-b border-[#E5E7EB] flex-shrink-0">
              <button
                onClick={() => setActiveTab("map")}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === "map"
                    ? "border-[#1E3A8A] text-[#1E3A8A]"
                    : "border-transparent text-[#9CA3AF] hover:text-[#374151]"
                }`}
              >
                <i className="ri-map-pin-line text-base"></i>
                지도
              </button>

              <button
                onClick={() => setActiveTab("schedule")}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === "schedule"
                    ? "border-[#1E3A8A] text-[#1E3A8A]"
                    : "border-transparent text-[#9CA3AF] hover:text-[#374151]"
                }`}
              >
                <i className="ri-calendar-schedule-line text-base"></i>
                일정
                {selectedDate && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB] flex-shrink-0"></span>
                )}
              </button>

              {selectedDate && (
                <div className="ml-auto flex items-center px-4">
                  <span className="text-xs text-[#9CA3AF]">
                    {(() => {
                      const d = new Date(selectedDate);
                      const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
                      return `${d.getMonth() + 1}월 ${d.getDate()}일 (${dayNames[d.getDay()]}) 선택됨`;
                    })()}
                  </span>
                </div>
              )}
            </div>

            {activeTab === "map" ? (
              <div className="flex-1 min-h-0">
                <ShopNaverMap
                  slots={slots}
                  style={{ height: "100%", width: "100%" }}
                  selectedShopIds={selectedShopIds.map((id) => Number(id))}
                  tourSize={tourSize}
                  date={selectedDate}
                />
              </div>
            ) : (
              <CombinationWrap>
                <div style={{overflowY: "auto",}}>
                  <DailyCombinationSection
                    date={selectedDate}
                    fixedShopIds={fixedShopIds}
                    selectedShopIds={selectedVendors.map((v) => v.id)}
                    tourSize={tourSize}
                  />
                </div>
              </CombinationWrap>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="bg-[#F5F6F8]">
            <PlannerAvailabilityCalendar
              currentMonth={currentMonth}
              availability={dailyAvailCountMap}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              onDateClick={handleDateClick}
              selectedDate={selectedDate}
            />
            
          </div>

          <div className="bg-white border-t border-gray-100">
            <div className="px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <i className="ri-map-pin-line text-base text-gray-700"></i>
                <h3 className="text-sm font-semibold text-gray-900">업체 위치</h3>
              </div>
            </div>

            <div className="h-[320px]">
              <ShopNaverMap
                slots={slots}
                style={{ height: "100%", width: "100%" }}
                selectedShopIds={selectedShopIds.map((id) => Number(id))}
                tourSize={tourSize}
                date={selectedDate}
              />
            </div>
          </div>
        </div>
      )}

      {showVendorModal && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowVendorModal(false)}
          ></div>

          <div className="relative bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[80vh]">
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-200 rounded-full"></div>
            </div>

            <div className="px-5 py-3 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">업체 선택</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                최대 3개까지 선택 가능합니다
              </p>
            </div>

            <div className="px-5 py-3 border-b border-gray-100">
              <div className="relative">
                <input
                  type="text"
                  placeholder="업체명을 입력하세요"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2.5 pr-10 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
                <i className="ri-search-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              </div>
            </div>

            {modalVendors.length > 0 && (
              <div className="px-5 py-2 border-b border-gray-100 flex flex-wrap gap-2">
                {modalVendors.map((vendor) => (
                  <div
                    key={vendor.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
                  >
                    <span className="whitespace-nowrap">{vendor.shopName}</span>
                    <button
                      onClick={() => toggleModalVendor(vendor)}
                      className="w-4 h-4 flex items-center justify-center hover:bg-blue-100 rounded-full cursor-pointer"
                    >
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex-1 overflow-y-auto">
              {filteredVendors.map((shop, index) => {
                const isSelected = modalVendors.some(
                  (vendor) => vendor.id === shop.id
                );
                const isDisabled = !isSelected && modalVendors.length >= 3;

                return (
                  <div key={shop.id}>
                    <button
                      onClick={() => !isDisabled && toggleModalVendor(shop)}
                      disabled={isDisabled}
                      className={`w-full px-5 py-4 flex items-center justify-between transition-colors ${
                        isDisabled
                          ? "opacity-40 cursor-not-allowed"
                          : "cursor-pointer hover:bg-gray-50"
                      }`}
                    >
                      <span
                        className={`text-sm font-medium ${
                          isSelected ? "text-blue-600" : "text-gray-900"
                        }`}
                      >
                        {shop.shopName}
                      </span>
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          isSelected
                            ? "bg-blue-600 border-blue-600"
                            : "border-gray-300"
                        }`}
                      >
                        {isSelected && (
                          <i className="ri-check-line text-white text-sm"></i>
                        )}
                      </div>
                    </button>

                    {index < filteredVendors.length - 1 && (
                      <div className="mx-5 border-b border-gray-100"></div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="px-5 py-4 border-t border-gray-100">
              <button
                onClick={applyVendors}
                disabled={modalVendors.length === 0}
                className={`w-full py-4 font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap ${
                  modalVendors.length > 0
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                적용하기 ({modalVendors.length}개 선택)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}